import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { CheckCircle, AlertTriangle, Eraser } from 'lucide-react';
import SignedSignatureImage from './SignedSignatureImage';
import { formatUserError } from './errorMessage';
import { trackAction } from './usage';

const POSITION_OPTIONS = ['1B', '2B', '3B', 'SS', 'OF', 'P', 'C'];
const BATS_THROWS_OPTIONS = ['R/R', 'L/L', 'R/L', 'L/R'];

// Renders an uploaded contract document reliably across file types.
// PDFs render natively in the browser; Office docs (docx/doc/ppt/xls) use the
// Microsoft Office Online viewer; anything else (or a viewer that can't load)
// falls back to a prominent download/open link so review never silently fails.
function ContractDocViewer({ doc }) {
  if (!doc) return null;
  const ext = (doc.ext || '').toLowerCase();
  const isPdf = ext === 'pdf';
  const isOffice = ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="border-b border-gray-200 p-4 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
        <a
          href={doc.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="text-sm text-blue-600 hover:underline whitespace-nowrap"
        >
          Download / Open in new tab
        </a>
      </div>
      {isPdf ? (
        <iframe
          title="Player Contract"
          src={doc.signedUrl}
          className="w-full"
          style={{ height: '720px', border: 0 }}
        />
      ) : isOffice ? (
        <iframe
          title="Player Contract"
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(doc.signedUrl)}`}
          className="w-full"
          style={{ height: '720px', border: 0 }}
        />
      ) : (
        <div className="p-8 text-center">
          <p className="text-gray-600 mb-4">This document can't be previewed inline.</p>
          <a
            href={doc.signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-block bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition"
          >
            Open the contract to review
          </a>
        </div>
      )}
      <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
        <p className="text-xs text-gray-500">
          Trouble viewing the contract above?{' '}
          <a href={doc.signedUrl} target="_blank" rel="noopener noreferrer" download className="text-blue-600 hover:underline">
            Open it in a new tab
          </a>{' '}to review before signing.
        </p>
      </div>
    </div>
  );
}

const SIZE_OPTIONS = {
  shorts: ['YS', 'YM', 'YL', 'YXL', 'AS', 'AM', 'AL', 'AXL', 'A2XL'],
  pants: ['YS', 'YM', 'YL', 'YXL', 'AS', 'AM', 'AL', 'AXL', 'A2XL'],
  belt: ['Youth', 'Adult S', 'Adult M', 'Adult L', 'Adult XL'],
  shirt: ['YS', 'YM', 'YL', 'YXL', 'AS', 'AM', 'AL', 'AXL', 'A2XL'],
  sweatshirt: ['YS', 'YM', 'YL', 'YXL', 'AS', 'AM', 'AL', 'AXL', 'A2XL'],
  helmet: ['6 1/4', '6 3/8', '6 1/2', '6 5/8', '6 3/4', '6 7/8', '7', '7 1/8', '7 1/4', '7 3/8', '7 1/2'],
  hat: ['YS/M', 'YM/L', 'AS/M', 'AM/L', 'AL/XL'],
};

export default function ContractPage({ userId, userRole, onSigned }) {
  const [loading, setLoading] = useState(true);
  const [existingContract, setExistingContract] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contractDoc, setContractDoc] = useState(null); // { title, signedUrl }

  // Player Info
  const [playerName, setPlayerName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [positions, setPositions] = useState([]);
  const [batsThrows, setBatsThrows] = useState('');
  const [gradYear, setGradYear] = useState('');
  const [highSchool, setHighSchool] = useState('');
  const [gpa, setGpa] = useState('');

  // Family Contact
  const [primaryParentName, setPrimaryParentName] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryParentName, setSecondaryParentName] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');

  // Sizing
  const [shortsSize, setShortsSize] = useState('');
  const [pantsSize, setPantsSize] = useState('');
  const [beltSize, setBeltSize] = useState('');
  const [shirtSize, setShirtSize] = useState('');
  const [sweatshirtSize, setSweatshirtSize] = useState('');
  const [helmetSize, setHelmetSize] = useState('');
  const [hatSize, setHatSize] = useState('');

  // Medical Consent
  const [consentParentFirst, setConsentParentFirst] = useState('');
  const [consentParentLast, setConsentParentLast] = useState('');
  const [playerFirst, setPlayerFirst] = useState('');
  const [playerLast, setPlayerLast] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [zip, setZip] = useState('');
  const [allergies, setAllergies] = useState('');
  const [specialMedications, setSpecialMedications] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insurancePolicy, setInsurancePolicy] = useState('');
  const [preferredHospital, setPreferredHospital] = useState('');

  // Signatures
  const [playerSigFirst, setPlayerSigFirst] = useState('');
  const [playerSigLast, setPlayerSigLast] = useState('');
  const [parentSigFirst, setParentSigFirst] = useState('');
  const [parentSigLast, setParentSigLast] = useState('');

  // Canvas refs
  const playerCanvasRef = useRef(null);
  const parentCanvasRef = useRef(null);
  const [playerDrawing, setPlayerDrawing] = useState(false);
  const [parentDrawing, setParentDrawing] = useState(false);
  const [playerHasSignature, setPlayerHasSignature] = useState(false);
  const [parentHasSignature, setParentHasSignature] = useState(false);

  useEffect(() => {
    fetchContract();
    fetchContractDocument();
  }, [userId]);

  const fetchContractDocument = async () => {
    try {
      const { data: rows, error } = await supabase
        .from('staff_documents')
        .select('id, title, file_path, created_at')
        .ilike('title', '%Naturals Player Contract%')
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = rows && rows[0];
      if (!row) return;
      const { data: signed, error: sErr } = await supabase.storage
        .from('staff-documents')
        .createSignedUrl(row.file_path, 60 * 60);
      if (sErr || !signed?.signedUrl) return;
      // Derive the extension from the stored file path so the viewer can pick
      // the right rendering strategy (native PDF vs Office viewer vs download).
      const ext = (row.file_path || '').split('.').pop().split('?')[0].toLowerCase();
      setContractDoc({ title: row.title, signedUrl: signed.signedUrl, ext });
    } catch (e) {
      console.error('Error loading contract document:', e);
    }
  };

  const fetchContract = async () => {
    try {
      const { data, error } = await supabase
        .from('player_contracts')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      setExistingContract(data);
    } catch (error) {
      console.error('Error fetching contract:', error);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing helpers (same as WaiverPage)
  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (!loading && !existingContract) {
      initCanvas(playerCanvasRef.current);
      initCanvas(parentCanvasRef.current);
    }
  }, [loading, existingContract, initCanvas]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e, canvasRef, setDrawing) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  };

  const draw = (e, canvasRef, drawing, setHasSig) => {
    e.preventDefault();
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSig(true);
  };

  const stopDraw = (setDrawing) => { setDrawing(false); };

  const clearCanvas = (canvasRef, setHasSig) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
  };

  const canvasToBlob = (canvasRef) => {
    return new Promise((resolve) => {
      canvasRef.current.toBlob(resolve, 'image/png');
    });
  };

  const togglePosition = (pos) => {
    setPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
  };

  const handleSubmit = async () => {
    if (!playerName.trim()) return alert('Please enter the player name.');
    if (!playerHasSignature) return alert('Please provide the player signature.');
    if (!parentHasSignature) return alert('Please provide the parent/guardian signature.');
    if (!consentParentFirst.trim() || !consentParentLast.trim()) return alert('Please fill in parent/guardian name in the medical consent section.');
    if (!playerFirst.trim() || !playerLast.trim()) return alert('Please fill in player name in the medical consent section.');
    if (!playerSigFirst.trim() || !playerSigLast.trim()) return alert('Please enter the player\'s printed name for the signature.');
    if (!parentSigFirst.trim() || !parentSigLast.trim()) return alert('Please enter the parent/guardian\'s printed name for the signature.');
    trackAction('submit_contract');

    setSubmitting(true);
    // Track uploaded paths so we can clean up on any downstream failure.
    const uploadedPaths = [];
    try {
      const timestamp = Date.now();

      // Upload player signature
      const playerBlob = await canvasToBlob(playerCanvasRef);
      const playerPath = `${userId}/contract-player-${timestamp}.png`;
      const { error: pErr } = await supabase.storage
        .from('signatures')
        .upload(playerPath, playerBlob, { contentType: 'image/png', upsert: true });
      if (pErr) throw pErr;
      uploadedPaths.push(playerPath);

      // Upload parent signature
      const parentBlob = await canvasToBlob(parentCanvasRef);
      const parentPath = `${userId}/contract-parent-${timestamp}.png`;
      const { error: gErr } = await supabase.storage
        .from('signatures')
        .upload(parentPath, parentBlob, { contentType: 'image/png', upsert: true });
      if (gErr) throw gErr;
      uploadedPaths.push(parentPath);

      const { error: insertErr } = await supabase
        .from('player_contracts')
        .insert({
          user_id: userId,
          player_name: playerName.trim(),
          birthdate: birthdate || null,
          positions: positions.length > 0 ? positions : null,
          bats_throws: batsThrows || null,
          grad_year: gradYear || null,
          high_school: highSchool.trim() || null,
          gpa: gpa.trim() || null,
          primary_parent_name: primaryParentName.trim() || null,
          primary_phone: primaryPhone.trim() || null,
          primary_email: primaryEmail.trim() || null,
          secondary_parent_name: secondaryParentName.trim() || null,
          secondary_phone: secondaryPhone.trim() || null,
          secondary_email: secondaryEmail.trim() || null,
          shorts_size: shortsSize || null,
          pants_size: pantsSize || null,
          belt_size: beltSize || null,
          shirt_size: shirtSize || null,
          sweatshirt_size: sweatshirtSize || null,
          helmet_size: helmetSize || null,
          hat_size: hatSize || null,
          conduct_agreed: true,
          consent_parent_first: consentParentFirst.trim() || null,
          consent_parent_last: consentParentLast.trim() || null,
          player_first: playerFirst.trim() || null,
          player_last: playerLast.trim() || null,
          address: address.trim() || null,
          city: city.trim() || null,
          state: stateName.trim() || null,
          zip: zip.trim() || null,
          allergies: allergies.trim() || null,
          special_medications: specialMedications.trim() || null,
          insurance_provider: insuranceProvider.trim() || null,
          insurance_policy: insurancePolicy.trim() || null,
          preferred_hospital: preferredHospital.trim() || null,
          player_signature_url: playerPath,
          parent_signature_url: parentPath,
        });

      if (insertErr) throw insertErr;

      await fetchContract();
      if (onSigned) onSigned();
      alert('Contract signed successfully!');
    } catch (error) {
      console.error('Error submitting contract:', error);
      // Roll back any orphaned signature uploads so they don't sit in storage
      // unreferenced after a downstream failure.
      if (uploadedPaths.length > 0) {
        await supabase.storage.from('signatures').remove(uploadedPaths).catch(() => {});
      }
      alert('Error submitting contract: ' + formatUserError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">Loading contract...</p>
      </div>
    );
  }

  // Already signed view
  if (existingContract) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Player Contract</h2>
          <p className="text-gray-600 mt-1">Naturals Select 2026-2027 Season</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
          <div>
            <p className="font-semibold text-green-800">Contract Signed</p>
            <p className="text-sm text-green-700">
              Signed on {new Date(existingContract.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <ContractDocViewer doc={contractDoc} />

        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Player Info Summary */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Player Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Player Name</p>
                <p className="text-gray-900 font-medium">{existingContract.player_name}</p>
              </div>
              {existingContract.birthdate && (
                <div>
                  <p className="text-sm text-gray-600">Birthdate</p>
                  <p className="text-gray-900 font-medium">{existingContract.birthdate}</p>
                </div>
              )}
              {existingContract.positions?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Positions</p>
                  <p className="text-gray-900 font-medium">{existingContract.positions.join(', ')}</p>
                </div>
              )}
              {existingContract.bats_throws && (
                <div>
                  <p className="text-sm text-gray-600">Bats/Throws</p>
                  <p className="text-gray-900 font-medium">{existingContract.bats_throws}</p>
                </div>
              )}
              {existingContract.grad_year && (
                <div>
                  <p className="text-sm text-gray-600">Grad Year</p>
                  <p className="text-gray-900 font-medium">{existingContract.grad_year}</p>
                </div>
              )}
              {existingContract.high_school && (
                <div>
                  <p className="text-sm text-gray-600">High School</p>
                  <p className="text-gray-900 font-medium">{existingContract.high_school}</p>
                </div>
              )}
              {existingContract.gpa && (
                <div>
                  <p className="text-sm text-gray-600">GPA</p>
                  <p className="text-gray-900 font-medium">{existingContract.gpa}</p>
                </div>
              )}
            </div>
          </div>

          {/* Family Contact Summary */}
          {existingContract.primary_parent_name && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Family Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Primary Parent/Guardian</p>
                  <p className="text-gray-900 font-medium">{existingContract.primary_parent_name}</p>
                </div>
                {existingContract.primary_phone && (
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-gray-900 font-medium">{existingContract.primary_phone}</p>
                  </div>
                )}
                {existingContract.primary_email && (
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-gray-900 font-medium">{existingContract.primary_email}</p>
                  </div>
                )}
              </div>
              {existingContract.secondary_parent_name && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div>
                    <p className="text-sm text-gray-600">Secondary Parent/Guardian</p>
                    <p className="text-gray-900 font-medium">{existingContract.secondary_parent_name}</p>
                  </div>
                  {existingContract.secondary_phone && (
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="text-gray-900 font-medium">{existingContract.secondary_phone}</p>
                    </div>
                  )}
                  {existingContract.secondary_email && (
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-gray-900 font-medium">{existingContract.secondary_email}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Signatures */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Signatures</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Player Signature</p>
                <SignedSignatureImage
                  signatureValue={existingContract.player_signature_url}
                  alt="Player Signature"
                  className="border border-gray-200 rounded bg-white max-h-24"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Parent/Guardian Signature</p>
                <SignedSignatureImage
                  signatureValue={existingContract.parent_signature_url}
                  alt="Parent Signature"
                  className="border border-gray-200 rounded bg-white max-h-24"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unsigned view — signing form
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Player Contract</h2>
        <p className="text-gray-600 mt-1">Naturals Select 2026-2027 Season</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3">
        <AlertTriangle className="text-yellow-600 flex-shrink-0" size={24} />
        <p className="text-sm text-yellow-800">Please complete all sections and sign the contract below.</p>
      </div>

      <ContractDocViewer doc={contractDoc} />

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 space-y-8">

          {/* Section 1: Player Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Player Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Player Full Name *</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter player's full name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birthdate</label>
                  <input
                    type="date"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                  <input
                    type="text"
                    value={gradYear}
                    onChange={(e) => setGradYear(e.target.value)}
                    placeholder="e.g. 2028"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GPA</label>
                  <input
                    type="text"
                    value={gpa}
                    onChange={(e) => setGpa(e.target.value)}
                    placeholder="e.g. 3.5"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">High School</label>
                <input
                  type="text"
                  value={highSchool}
                  onChange={(e) => setHighSchool(e.target.value)}
                  placeholder="Enter high school name"
                  className="w-full md:w-1/2 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Positions</label>
                <div className="flex flex-wrap gap-3">
                  {POSITION_OPTIONS.map(pos => (
                    <label key={pos} className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={positions.includes(pos)}
                        onChange={() => togglePosition(pos)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{pos}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bats / Throws</label>
                <div className="flex flex-wrap gap-4">
                  {BATS_THROWS_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="batsThrows"
                        checked={batsThrows === opt}
                        onChange={() => setBatsThrows(opt)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Family Contact */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Contact</h3>
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-600">Primary Parent / Guardian</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={primaryParentName} onChange={(e) => setPrimaryParentName(e.target.value)} placeholder="Full name" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={primaryPhone} onChange={(e) => setPrimaryPhone(e.target.value)} placeholder="Phone number" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={primaryEmail} onChange={(e) => setPrimaryEmail(e.target.value)} placeholder="Email address" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <p className="text-sm font-medium text-gray-600 mt-4">Secondary Parent / Guardian</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={secondaryParentName} onChange={(e) => setSecondaryParentName(e.target.value)} placeholder="Full name" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)} placeholder="Phone number" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={secondaryEmail} onChange={(e) => setSecondaryEmail(e.target.value)} placeholder="Email address" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Uniform & Equipment Sizing */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Uniform & Equipment Sizing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { label: 'Shorts', value: shortsSize, setter: setShortsSize, options: SIZE_OPTIONS.shorts },
                { label: 'Pants', value: pantsSize, setter: setPantsSize, options: SIZE_OPTIONS.pants },
                { label: 'Belt', value: beltSize, setter: setBeltSize, options: SIZE_OPTIONS.belt },
                { label: 'Shirt / Jersey', value: shirtSize, setter: setShirtSize, options: SIZE_OPTIONS.shirt },
                { label: 'Sweatshirt', value: sweatshirtSize, setter: setSweatshirtSize, options: SIZE_OPTIONS.sweatshirt },
                { label: 'Helmet', value: helmetSize, setter: setHelmetSize, options: SIZE_OPTIONS.helmet },
                { label: 'Hat', value: hatSize, setter: setHatSize, options: SIZE_OPTIONS.hat },
              ].map(({ label, value, setter, options }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                  >
                    <option value="">Select size</option>
                    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Consent to Treat Minor */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consent to Treat Minor</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian First Name *</label>
                  <input type="text" value={consentParentFirst} onChange={(e) => setConsentParentFirst(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent/Guardian Last Name *</label>
                  <input type="text" value={consentParentLast} onChange={(e) => setConsentParentLast(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Player First Name *</label>
                  <input type="text" value={playerFirst} onChange={(e) => setPlayerFirst(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Player Last Name *</label>
                  <input type="text" value={playerLast} onChange={(e) => setPlayerLast(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" value={stateName} onChange={(e) => setStateName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                  <input type="text" value={zip} onChange={(e) => setZip(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                  <input type="text" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="List any allergies or 'None'" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Medications</label>
                  <input type="text" value={specialMedications} onChange={(e) => setSpecialMedications(e.target.value)} placeholder="List any medications or 'None'" className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Provider</label>
                  <input type="text" value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy #</label>
                  <input type="text" value={insurancePolicy} onChange={(e) => setInsurancePolicy(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Hospital</label>
                  <input type="text" value={preferredHospital} onChange={(e) => setPreferredHospital(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Signatures */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Signatures</h3>

            {/* Player Signature */}
            <div className="space-y-4 mb-8">
              <p className="text-sm font-semibold text-gray-700">Player Signature</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" value={playerSigFirst} onChange={(e) => setPlayerSigFirst(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" value={playerSigLast} onChange={(e) => setPlayerSigLast(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature *</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={playerCanvasRef}
                    className="w-full cursor-crosshair touch-none"
                    style={{ height: '120px' }}
                    onMouseDown={(e) => startDraw(e, playerCanvasRef, setPlayerDrawing)}
                    onMouseMove={(e) => draw(e, playerCanvasRef, playerDrawing, setPlayerHasSignature)}
                    onMouseUp={() => stopDraw(setPlayerDrawing)}
                    onMouseLeave={() => stopDraw(setPlayerDrawing)}
                    onTouchStart={(e) => startDraw(e, playerCanvasRef, setPlayerDrawing)}
                    onTouchMove={(e) => draw(e, playerCanvasRef, playerDrawing, setPlayerHasSignature)}
                    onTouchEnd={() => stopDraw(setPlayerDrawing)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => clearCanvas(playerCanvasRef, setPlayerHasSignature)}
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                >
                  <Eraser size={14} />
                  <span>Clear Signature</span>
                </button>
              </div>
              <p className="text-sm text-gray-500">Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>

            {/* Parent/Guardian Signature */}
            <div className="space-y-4">
              <p className="text-sm font-semibold text-gray-700">Parent / Guardian Signature</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" value={parentSigFirst} onChange={(e) => setParentSigFirst(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" value={parentSigLast} onChange={(e) => setParentSigLast(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature *</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={parentCanvasRef}
                    className="w-full cursor-crosshair touch-none"
                    style={{ height: '120px' }}
                    onMouseDown={(e) => startDraw(e, parentCanvasRef, setParentDrawing)}
                    onMouseMove={(e) => draw(e, parentCanvasRef, parentDrawing, setParentHasSignature)}
                    onMouseUp={() => stopDraw(setParentDrawing)}
                    onMouseLeave={() => stopDraw(setParentDrawing)}
                    onTouchStart={(e) => startDraw(e, parentCanvasRef, setParentDrawing)}
                    onTouchMove={(e) => draw(e, parentCanvasRef, parentDrawing, setParentHasSignature)}
                    onTouchEnd={() => stopDraw(setParentDrawing)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => clearCanvas(parentCanvasRef, setParentHasSignature)}
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                >
                  <Eraser size={14} />
                  <span>Clear Signature</span>
                </button>
              </div>
              <p className="text-sm text-gray-500">Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>

          {/* Submit */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Sign Contract'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
